// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Keeping the screen awake while a ball is in play (M3-41).
//
// **A phone dims and then sleeps after its idle timeout, and a pinball player gives it nothing to
// count as activity.** The controls are `touchstart` on a canvas and a thumb held still on a
// flipper zone; none of it is a scroll, a tap on a link or a keypress, which is what a browser's
// idle timer is watching for. So the failure is: a long ball, the screen dims, the player taps to
// wake it, the tap is a flipper press they did not mean, and the ball drains. It is the single
// most common way a web game on a phone feels broken, and it is invisible on a desktop.
//
// WHAT IT IS NOT
// --------------
// **Not held for the life of the page.** A wake lock is a battery cost and a promise to the person
// holding the device, so it exists only while a machine is actually running — not on the arcade
// list, not on Settings, and not while a game sits finished on the glass.
//
// **Not an error when it is refused.** `navigator.wakeLock` is absent on iOS before 16.4, refused
// in a background tab, and refused outright on low battery by some Android builds. Every one of
// those is a device saying no to a nicety, and a machine that surfaced it would be reporting a
// fault about something the player never asked for. The request is fire-and-forget and its
// rejection is swallowed on purpose.
//
// **Re-acquired rather than assumed.** The platform releases the lock whenever the document is
// hidden — a notification, the app switcher, the screen being locked by hand — and it does *not*
// give it back on return. Without the visibility listener below, a wake lock works until the first
// interruption of a session and silently stops working for the rest of it, which is worse than not
// having one: it works in every test and fails in the long game it was for.

/** The sliver of `WakeLockSentinel` this uses. A real one satisfies it. */
export interface WakeLockSentinelLike {
  release: () => Promise<void>
  readonly released?: boolean
}

/** The sliver of `navigator.wakeLock`. */
export interface WakeLockHost {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>
}

/** Where the host and the visibility state are read from. The window, in the product. */
export interface WakeLockEnvironment {
  readonly wakeLock?: WakeLockHost | undefined
  /** `document.visibilityState`, read fresh each time. */
  readonly visible: () => boolean
  readonly addEventListener: (type: string, listener: () => void) => void
  readonly removeEventListener: (type: string, listener: () => void) => void
}

/** Read the browser's own, or nothing where there is none. */
export function browserWakeLock(): WakeLockEnvironment | undefined {
  const nav = (globalThis as { navigator?: { wakeLock?: WakeLockHost } }).navigator
  const doc = (globalThis as {
    document?: {
      visibilityState?: string
      addEventListener: (type: string, listener: () => void) => void
      removeEventListener: (type: string, listener: () => void) => void
    }
  }).document
  if (doc === undefined) return undefined
  return {
    // `undefined` rather than absent is deliberate: a browser with no `wakeLock` still gets an
    // environment, so the caller's code path is the same everywhere and the *only* difference is
    // that requesting does nothing. A `ScreenWake` that could not be constructed would make every
    // call site test for it.
    wakeLock: nav?.wakeLock,
    visible: () => doc.visibilityState !== 'hidden',
    addEventListener: (type, listener) => doc.addEventListener(type, listener),
    removeEventListener: (type, listener) => doc.removeEventListener(type, listener),
  }
}

/**
 * Holds a screen wake lock while it is wanted and the page is visible.
 *
 * Two booleans, and they are genuinely different: *wanted* is what the game asked for, *held* is
 * what the platform granted. Collapsing them would lose the case this class exists for — wanted
 * and not held, because the document was hidden, which is exactly the state a return to the tab
 * has to resolve.
 */
export class ScreenWake {
  private sentinel: WakeLockSentinelLike | undefined
  private wanted = false
  private disposed = false
  private readonly onVisibility: () => void

  constructor(private readonly env: WakeLockEnvironment) {
    this.onVisibility = () => {
      if (this.disposed) return
      // Coming back: ask again, because the platform released it on the way out and will not
      // hand it back. Going away: forget the sentinel, which is already dead — releasing it
      // explicitly would be a promise rejection about an object the platform has finished with.
      if (this.env.visible()) {
        if (this.wanted) void this.acquire()
      } else {
        this.sentinel = undefined
      }
    }
    this.env.addEventListener('visibilitychange', this.onVisibility)
  }

  /** True when the platform has actually granted one. For tests and for a diagnostics line. */
  get held(): boolean {
    return this.sentinel !== undefined
  }

  /** Ask for the screen to stay awake. Idempotent; safe to call every frame. */
  request(): void {
    if (this.disposed || this.wanted) return
    this.wanted = true
    if (this.env.visible()) void this.acquire()
  }

  /** Stop wanting it, and give back anything held. */
  release(): void {
    this.wanted = false
    const held = this.sentinel
    this.sentinel = undefined
    // Swallowed: releasing a sentinel the platform has already released rejects, and there is
    // nothing a player could do about either outcome.
    void held?.release().catch(() => undefined)
  }

  /** Take the listener off and let go. Called when the machine is disposed. */
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.release()
    this.env.removeEventListener('visibilitychange', this.onVisibility)
  }

  private async acquire(): Promise<void> {
    if (this.sentinel !== undefined || this.env.wakeLock === undefined) return
    try {
      const sentinel = await this.env.wakeLock.request('screen')
      // The await is a gap, and three things can have happened across it: the game ended, the
      // machine was disposed, or the tab was hidden. Holding a lock nobody wants any more is the
      // battery cost this class is supposed to be careful with.
      if (!this.wanted || this.disposed) {
        void sentinel.release().catch(() => undefined)
        return
      }
      this.sentinel = sentinel
    } catch {
      // Refused. Not a fault — see the header.
    }
  }
}
